import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    FiChevronDown,
    FiChevronRight,
    FiEdit2,
    FiFolderPlus,
    FiLoader,
    FiMove,
    FiPlus,
    FiTrash2,
    FiX,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { Header } from '../../components/layout/Header';
import { Sidebar } from '../../components/layout/Sidebar';
import { SearchableSelect, confirmDialog, toast } from '../../components/ui';
import { useAuth } from '../../contexts';
import { usePreferences } from '../../contexts/PreferencesContext';
import { api } from '../../services/api';
import styles from './PlanoContas.module.css';

type TabType = 'categorias' | 'formasPagamento';
type TipoMovimento = 'ENTRADA' | 'SAIDA' | 'AMBOS';
type ModalType = 'grupo' | 'categoria' | 'formaPagamento';

/**
 * Vocabulário da tela (o modelo no banco mantém os nomes antigos):
 *   Grupo     -> model Categoria     (agrupa categorias dentro de Receitas/Despesas)
 *   Categoria -> model SubCategoria  (o que o usuário escolhe ao lançar)
 */
interface Categoria {
    id: string;
    categoriaId: string;
    nome: string;
    descricao?: string;
    ordem: number;
    ativo: boolean;
    lancamentosVinculados?: number;
}

interface Grupo {
    id: string;
    tipo: TipoMovimento;
    nome: string;
    descricao?: string;
    ordem: number;
    ativo: boolean;
    categorias: Categoria[];
    lancamentosVinculados?: number;
}

interface FormaPagamento {
    id: string;
    tipo: TipoMovimento;
    nome: string;
    descricao?: string;
    ordem: number;
    ativo: boolean;
    lancamentosVinculados?: number;
}

interface PlanoContasResumo {
    totalGrupos: number;
    totalCategorias: number;
    totalFormasPagamento: number;
    categoriasEntrada: number;
    categoriasSaida: number;
}

/** Campos que o modal de edicao le, comuns a grupo, categoria e forma de pagamento. */
interface ItemEditavel {
    id: string;
    nome: string;
    descricao?: string;
    tipo?: TipoMovimento;
    categoriaId?: string;
    lancamentosVinculados?: number;
}

/** Item sendo arrastado no modo "Organizar grupos". */
type Arrasto =
    | { kind: 'categoria'; id: string; tipo: TipoMovimento; grupoId: string }
    | { kind: 'grupo'; id: string; tipo: TipoMovimento };

const ehReceita = (tipo: TipoMovimento) => tipo === 'ENTRADA';

const clonarGrupos = (grupos: Grupo[]): Grupo[] =>
    grupos.map((g) => ({ ...g, categorias: g.categorias.map((c) => ({ ...c })) }));

/** Ordem global usada pelo Fluxo de Caixa: receitas primeiro, depois despesas. */
const sequenciaGlobal = (grupos: Grupo[]): string[] => [
    ...grupos.filter((g) => ehReceita(g.tipo)).map((g) => g.id),
    ...grupos.filter((g) => !ehReceita(g.tipo)).map((g) => g.id),
];

/**
 * Quantos itens precisaram sair do lugar entre duas ordenações dos mesmos ids.
 * Usa a maior subsequência comum: arrastar um grupo conta como 1 alteração, e não
 * como uma por vizinho deslocado.
 */
const itensForaDeLugar = (antes: string[], depois: string[]): number => {
    const m = antes.length;
    const n = depois.length;
    const tabela: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));

    for (let i = m - 1; i >= 0; i--) {
        for (let j = n - 1; j >= 0; j--) {
            tabela[i][j] = antes[i] === depois[j]
                ? tabela[i + 1][j + 1] + 1
                : Math.max(tabela[i + 1][j], tabela[i][j + 1]);
        }
    }

    return n - tabela[0][0];
};

/**
 * Reaplica o rascunho de organização sobre dados recém-carregados do servidor.
 * Sem isso, criar/editar/excluir uma categoria durante o modo "Organizar grupos"
 * deixaria o rascunho apontando para itens que não existem mais (ou ignorando os
 * novos), e o salvamento enviaria movimentações inválidas.
 */
const reconciliarRascunho = (novos: Grupo[], rascunhoAnterior: Grupo[]): Grupo[] => {
    const pendentePorCategoria = new Map<string, { grupoId: string; posicao: number }>();
    rascunhoAnterior.forEach((g) =>
        g.categorias.forEach((c, i) => pendentePorCategoria.set(c.id, { grupoId: g.id, posicao: i })),
    );

    const resultado = novos.map((g) => ({ ...g, categorias: [] as Categoria[] }));
    const porId = new Map(resultado.map((g) => [g.id, g]));

    for (const grupo of novos) {
        for (const categoria of grupo.categorias) {
            const pendente = pendentePorCategoria.get(categoria.id);
            const destino = pendente ? porId.get(pendente.grupoId) : undefined;
            // Um destino de tipo diferente deixou de ser válido (o grupo mudou de tipo).
            const alvo = destino && destino.tipo === grupo.tipo ? destino : porId.get(grupo.id)!;
            alvo.categorias.push({ ...categoria, categoriaId: alvo.id });
        }
    }

    const posicaoNoRascunho = (id: string) => pendentePorCategoria.get(id)?.posicao ?? Number.MAX_SAFE_INTEGER;
    for (const grupo of resultado) {
        grupo.categorias.sort((a, b) => posicaoNoRascunho(a.id) - posicaoNoRascunho(b.id));
    }

    // Ordem dos grupos segue o rascunho; grupos criados agora vão para o fim.
    const posicaoGrupo = new Map(rascunhoAnterior.map((g, i) => [g.id, i]));
    return resultado.sort(
        (a, b) =>
            (posicaoGrupo.get(a.id) ?? Number.MAX_SAFE_INTEGER) -
            (posicaoGrupo.get(b.id) ?? Number.MAX_SAFE_INTEGER),
    );
};

/**
 * Diferença entre o plano salvo e o rascunho do modo de organização.
 * Grupos com composição/ordem alterada têm TODAS as categorias reenviadas, para
 * que o campo `ordem` fique contíguo e o Fluxo de Caixa reflita exatamente a tela.
 */
const calcularAlteracoes = (base: Grupo[], atual: Grupo[]) => {
    const posOriginal = new Map<string, { grupoId: string; index: number }>();
    base.forEach((g) => g.categorias.forEach((c, i) => posOriginal.set(c.id, { grupoId: g.id, index: i })));
    const categoriasBasePorGrupo = new Map(base.map((g) => [g.id, g.categorias.map((c) => c.id)]));

    const movimentacoes: { id: string; categoriaId: string; ordem: number }[] = [];
    let categoriasAlteradas = 0;

    for (const grupo of atual) {
        const idsFinais = grupo.categorias.map((c) => c.id);
        const idsBase = categoriasBasePorGrupo.get(grupo.id) ?? [];

        const trocaramDeGrupo = grupo.categorias.filter(
            (c) => posOriginal.get(c.id)?.grupoId !== grupo.id,
        ).length;

        // Reordenação interna: compara só quem já estava aqui e continua aqui,
        // para não contar o deslocamento causado por uma entrada/saída.
        const permaneceramFinal = idsFinais.filter((id) => idsBase.includes(id));
        const permaneceramBase = idsBase.filter((id) => idsFinais.includes(id));
        const reordenouInternamente = permaneceramFinal.join('|') !== permaneceramBase.join('|');

        const saiuAlguem = idsBase.some((id) => !idsFinais.includes(id));
        if (trocaramDeGrupo === 0 && !reordenouInternamente && !saiuAlguem) continue;

        categoriasAlteradas += trocaramDeGrupo + (reordenouInternamente ? 1 : 0);
        grupo.categorias.forEach((c, i) => movimentacoes.push({ id: c.id, categoriaId: grupo.id, ordem: i }));
    }

    const seqBase = sequenciaGlobal(base);
    const seqAtual = sequenciaGlobal(atual);
    const gruposReordenados = seqBase.join('|') !== seqAtual.join('|');
    const ordensGrupo = gruposReordenados ? seqAtual.map((id, i) => ({ id, ordem: i })) : [];
    const gruposAlterados = gruposReordenados ? itensForaDeLugar(seqBase, seqAtual) : 0;

    return { movimentacoes, ordensGrupo, total: categoriasAlteradas + gruposAlterados };
};

export const PlanoContas = () => {
    const { t } = usePreferences();
    const navigate = useNavigate();
    const { filiais, hasPermission } = useAuth();

    // MAN-29: os controles de escrita respeitam o perfil de acesso.
    const canCreate = hasPermission('PLANO_CONTAS', 'CREATE');
    const canEdit = hasPermission('PLANO_CONTAS', 'EDIT');
    const canDelete = hasPermission('PLANO_CONTAS', 'DELETE');

    const nomeEmpresa = filiais[0]?.empresa.name;

    const [activeTab, setActiveTab] = useState<TabType>('categorias');
    const [loading, setLoading] = useState(true);
    const [grupos, setGrupos] = useState<Grupo[]>([]);
    const [formasPagamento, setFormasPagamento] = useState<FormaPagamento[]>([]);
    const [resumo, setResumo] = useState<PlanoContasResumo | null>(null);
    const [expandidos, setExpandidos] = useState<Set<string>>(new Set());

    // Modo "Organizar grupos": o rascunho é editado por arrasto e só vai ao servidor no salvar.
    const [organizando, setOrganizando] = useState(false);
    const [rascunho, setRascunho] = useState<Grupo[] | null>(null);
    const [arrasto, setArrasto] = useState<Arrasto | null>(null);
    const [alvoDrop, setAlvoDrop] = useState<string | null>(null);
    const [salvandoOrganizacao, setSalvandoOrganizacao] = useState(false);

    // Modal
    const [showModal, setShowModal] = useState(false);
    const [modalType, setModalType] = useState<ModalType>('categoria');
    const [editingItem, setEditingItem] = useState<ItemEditavel | null>(null);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        tipo: 'ENTRADA' as TipoMovimento,
        nome: '',
        descricao: '',
        grupoId: '',
    });

    const gruposExibidos = rascunho ?? grupos;

    const alteracoes = useMemo(
        () => (rascunho ? calcularAlteracoes(grupos, rascunho) : { movimentacoes: [], ordensGrupo: [], total: 0 }),
        [grupos, rascunho],
    );
    const temPendencias = alteracoes.total > 0;

    /** `silencioso` recarrega sem trocar a tela pelo spinner (usado após um CRUD). */
    const loadData = useCallback(async (silencioso = false) => {
        try {
            if (!silencioso) setLoading(true);
            const data = await api.getPlanoContasCompleto();
            const gruposApi = (data.categorias ?? []) as (Omit<Grupo, 'categorias'> & {
                subCategorias?: Categoria[];
            })[];
            const novos = gruposApi.map(({ subCategorias, ...grupo }) => ({
                ...grupo,
                categorias: subCategorias ?? [],
            }));

            setGrupos(novos);
            setRascunho((anterior) => (anterior ? reconciliarRascunho(novos, anterior) : null));
            setFormasPagamento(data.formasPagamento as FormaPagamento[]);
            setResumo(data.resumo);
        } catch (error) {
            console.error('Erro ao carregar plano de contas:', error);
        } finally {
            if (!silencioso) setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const toggleGrupo = (id: string) => {
        setExpandidos((prev) => {
            const novo = new Set(prev);
            if (novo.has(id)) novo.delete(id);
            else novo.add(id);
            return novo;
        });
    };

    // ==================== MODO ORGANIZAR ====================

    const salvarOrganizacao = useCallback(async (): Promise<boolean> => {
        if (!rascunho) return true;
        const { movimentacoes, ordensGrupo } = calcularAlteracoes(grupos, rascunho);
        if (movimentacoes.length === 0 && ordensGrupo.length === 0) return true;

        setSalvandoOrganizacao(true);
        try {
            // O movimento das categorias é transacional e é o que pode ser recusado
            // (tipo incompatível, nome duplicado). Vai primeiro para que uma recusa
            // não deixe a ordem dos grupos alterada pela metade.
            if (movimentacoes.length > 0) {
                await api.moverSubCategorias(movimentacoes);
            }
            if (ordensGrupo.length > 0) {
                await Promise.all(ordensGrupo.map(({ id, ordem }) => api.updateCategoria(id, { ordem })));
            }
            setRascunho(null);
            setOrganizando(false);
            await loadData();
            toast(t('plano.organizacaoSalva'), 'success');
            return true;
        } catch (error) {
            console.error('Erro ao salvar organização:', error);
            toast(error instanceof Error ? error.message : t('plano.erroMover'), 'error');
            return false;
        } finally {
            setSalvandoOrganizacao(false);
        }
    }, [grupos, rascunho, loadData, t]);

    const descartarOrganizacao = async () => {
        if (temPendencias) {
            const confirmado = await confirmDialog(t('plano.confirmarDescartar'));
            if (!confirmado) return;
        }
        setRascunho(null);
        setOrganizando(false);
        setArrasto(null);
        setAlvoDrop(null);
    };

    /** Liga/desliga o modo. Devolve `true` quando o modo ficou (ou já estava) desligado. */
    const alternarOrganizar = async (): Promise<boolean> => {
        if (!organizando) {
            setRascunho(clonarGrupos(grupos));
            setExpandidos(new Set(grupos.map((g) => g.id)));
            setOrganizando(true);
            return false;
        }

        if (temPendencias) {
            const salvar = await confirmDialog(t('plano.confirmarSairSemSalvar'));
            if (!salvar) return false;
            return salvarOrganizacao();
        }

        setRascunho(null);
        setOrganizando(false);
        return true;
    };

    /** Sair da aba Categorias com organização aberta encerraria o acesso ao botão salvar. */
    const trocarAba = async (aba: TabType) => {
        if (aba === activeTab) return;
        if (aba !== 'categorias' && organizando) {
            const encerrou = await alternarOrganizar();
            if (!encerrou) return;
        }
        setActiveTab(aba);
    };

    // Avisa antes de fechar/recarregar a aba com organização pendente.
    useEffect(() => {
        if (!temPendencias) return;
        const handler = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [temPendencias]);

    // Avisa antes de sair da tela por um link interno (menu lateral, header...).
    const guardaRef = useRef({ temPendencias, salvarOrganizacao, t });
    guardaRef.current = { temPendencias, salvarOrganizacao, t };

    useEffect(() => {
        if (!temPendencias) return;

        const onClickCapture = (e: MouseEvent) => {
            if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey) return;
            const link = (e.target as HTMLElement | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
            if (!link || link.target === '_blank') return;

            const href = link.getAttribute('href');
            if (!href || href.startsWith('#') || href.startsWith('http') || href === window.location.pathname) return;

            e.preventDefault();
            e.stopPropagation();

            void (async () => {
                const { salvarOrganizacao: salvar, t: traduzir } = guardaRef.current;
                const querSalvar = await confirmDialog(traduzir('plano.confirmarSairSemSalvar'));
                if (!querSalvar) return;
                const ok = await salvar();
                if (ok) navigate(href);
            })();
        };

        document.addEventListener('click', onClickCapture, true);
        return () => document.removeEventListener('click', onClickCapture, true);
    }, [temPendencias, navigate]);

    /** Move uma categoria para `grupoDestinoId`, na posição `indice` (ou no fim). */
    const moverCategoria = (categoriaId: string, grupoDestinoId: string, indice: number | null) => {
        setRascunho((prev) => {
            if (!prev) return prev;
            const copia = clonarGrupos(prev);

            const origem = copia.find((g) => g.categorias.some((c) => c.id === categoriaId));
            const destino = copia.find((g) => g.id === grupoDestinoId);
            if (!origem || !destino || origem.tipo !== destino.tipo) return prev;

            const posOrigem = origem.categorias.findIndex((c) => c.id === categoriaId);
            const [categoria] = origem.categorias.splice(posOrigem, 1);

            let posDestino = indice ?? destino.categorias.length;
            if (origem.id === destino.id && posOrigem < posDestino) posDestino -= 1;
            posDestino = Math.max(0, Math.min(posDestino, destino.categorias.length));

            destino.categorias.splice(posDestino, 0, { ...categoria, categoriaId: destino.id });
            return copia;
        });
    };

    /** Reordena um grupo, inserindo-o antes de `grupoAlvoId` (mesma seção). */
    const moverGrupo = (grupoId: string, grupoAlvoId: string) => {
        setRascunho((prev) => {
            if (!prev || grupoId === grupoAlvoId) return prev;
            const copia = clonarGrupos(prev);

            const origem = copia.findIndex((g) => g.id === grupoId);
            const alvo = copia.findIndex((g) => g.id === grupoAlvoId);
            if (origem < 0 || alvo < 0 || copia[origem].tipo !== copia[alvo].tipo) return prev;

            const [grupo] = copia.splice(origem, 1);
            const destino = copia.findIndex((g) => g.id === grupoAlvoId);
            copia.splice(destino, 0, grupo);
            return copia;
        });
    };

    const podeSoltarCategoria = (grupo: Grupo) =>
        organizando && arrasto?.kind === 'categoria' && arrasto.tipo === grupo.tipo;

    const podeSoltarGrupo = (grupo: Grupo) =>
        organizando && arrasto?.kind === 'grupo' && arrasto.tipo === grupo.tipo && arrasto.id !== grupo.id;

    /** Receitas e despesas não se misturam: o grupo fora do alcance fica apagado durante o arrasto. */
    const recusaArrasto = (grupo: Grupo) => arrasto !== null && arrasto.tipo !== grupo.tipo;

    const encerrarArrasto = () => {
        setArrasto(null);
        setAlvoDrop(null);
    };

    // ==================== CRUD ====================

    const openModal = (type: ModalType, item?: ItemEditavel) => {
        setModalType(type);
        setEditingItem(item || null);

        if (item) {
            setFormData({
                tipo: item.tipo || 'ENTRADA',
                nome: item.nome || '',
                descricao: item.descricao || '',
                grupoId: item.categoriaId || '',
            });
        } else {
            setFormData({
                tipo: 'ENTRADA',
                nome: '',
                descricao: '',
                grupoId: grupos[0]?.id || '',
            });
        }

        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingItem(null);
        setFormData({ tipo: 'ENTRADA', nome: '', descricao: '', grupoId: '' });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);

        try {
            if (modalType === 'grupo') {
                const payload = {
                    tipo: formData.tipo,
                    nome: formData.nome,
                    descricao: formData.descricao || undefined,
                };
                if (editingItem) {
                    await api.updateCategoria(editingItem.id, payload);
                } else {
                    // Novo grupo entra no fim da lista; a ordem fina é feita arrastando.
                    const proximaOrdem = grupos.length > 0 ? Math.max(...grupos.map((g) => g.ordem)) + 1 : 0;
                    await api.createCategoria({ ...payload, ordem: proximaOrdem });
                }
            } else if (modalType === 'categoria') {
                const payload = {
                    categoriaId: formData.grupoId,
                    nome: formData.nome,
                    descricao: formData.descricao || undefined,
                };
                if (editingItem) {
                    await api.updateSubCategoria(editingItem.id, payload);
                } else {
                    await api.createSubCategoria(payload);
                }
            } else {
                const payload = {
                    nome: formData.nome,
                    descricao: formData.descricao || undefined,
                };
                if (editingItem) {
                    await api.updateFormaPagamentoNorm(editingItem.id, payload);
                } else {
                    await api.createFormaPagamentoNorm(payload);
                }
            }

            await loadData(true);
            closeModal();
        } catch (error) {
            console.error('Erro ao salvar:', error);
            toast(error instanceof Error ? error.message : t('plano.erroSalvar'), 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (
        type: ModalType,
        item: { id: string; nome: string; lancamentosVinculados?: number; categorias?: Categoria[] },
    ) => {
        const vinculados = item.lancamentosVinculados ?? 0;
        if (vinculados > 0) {
            toast(t('plano.naoExcluirVinculado', { nome: t(item.nome), n: String(vinculados) }), 'error');
            return;
        }

        const filhas = type === 'grupo' ? item.categorias?.length ?? 0 : 0;
        const confirmed = await confirmDialog(
            filhas > 0
                ? t('plano.confirmarExcluirGrupo', { nome: t(item.nome), n: String(filhas) })
                : t('plano.confirmarExcluir'),
        );
        if (!confirmed) return;

        try {
            if (type === 'grupo') {
                await api.deleteCategoria(item.id);
            } else if (type === 'categoria') {
                await api.deleteSubCategoria(item.id);
            } else {
                await api.deleteFormaPagamentoNorm(item.id);
            }
            loadData(true);
        } catch (error) {
            console.error('Erro ao excluir:', error);
            toast(error instanceof Error ? error.message : t('plano.erroExcluir'), 'error');
        }
    };

    // ==================== RENDER ====================

    const contarCategorias = (n: number) =>
        t(n === 1 ? 'plano.catCount' : 'plano.catCount_plural', { n: String(n) });
    const contarGrupos = (n: number) =>
        t(n === 1 ? 'plano.grupoCount' : 'plano.grupoCount_plural', { n: String(n) });

    const renderCategoria = (grupo: Grupo, categoria: Categoria, indice: number) => {
        const bloqueada = (categoria.lancamentosVinculados ?? 0) > 0;
        const alvo = alvoDrop === `cat:${categoria.id}`;

        return (
            <div
                key={categoria.id}
                className={`${styles.categoriaRow} ${organizando ? styles.arrastavel : ''} ${alvo ? styles.dropAntes : ''}`}
                draggable={organizando}
                onDragStart={(e) => {
                    if (!organizando) return;
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', categoria.id);
                    setArrasto({ kind: 'categoria', id: categoria.id, tipo: grupo.tipo, grupoId: grupo.id });
                }}
                onDragEnd={encerrarArrasto}
                onDragOver={(e) => {
                    if (!podeSoltarCategoria(grupo)) return;
                    e.preventDefault();
                    e.stopPropagation();
                    setAlvoDrop(`cat:${categoria.id}`);
                }}
                onDrop={(e) => {
                    if (!podeSoltarCategoria(grupo) || !arrasto) return;
                    e.preventDefault();
                    e.stopPropagation();
                    moverCategoria(arrasto.id, grupo.id, indice);
                    encerrarArrasto();
                }}
            >
                <div className={styles.categoriaNome}>
                    {organizando && <FiMove className={styles.handle} size={14} aria-hidden />}
                    <span title={t(categoria.nome)}>{t(categoria.nome)}</span>
                </div>
                <div className={styles.actions}>
                    {canEdit && (
                        <button
                            type="button"
                            className={`${styles.actionBtn} ${styles.edit}`}
                            onClick={() => openModal('categoria', categoria)}
                            aria-label={t('plano.editarCategoria')}
                        >
                            <FiEdit2 size={16} />
                        </button>
                    )}
                    {canDelete && (
                        <button
                            type="button"
                            className={`${styles.actionBtn} ${styles.delete} ${bloqueada ? styles.disabled : ''}`}
                            title={bloqueada ? t('plano.exclusaoBloqueada') : undefined}
                            onClick={() => handleDelete('categoria', categoria)}
                            aria-label={t('common.excluir')}
                        >
                            <FiTrash2 size={16} />
                        </button>
                    )}
                </div>
            </div>
        );
    };

    const renderGrupo = (grupo: Grupo) => {
        const expandido = expandidos.has(grupo.id);
        const bloqueado = (grupo.lancamentosVinculados ?? 0) > 0;
        const alvoCategoria = alvoDrop === `grupo:${grupo.id}`;
        const alvoGrupo = alvoDrop === `ordem:${grupo.id}`;

        return (
            <div
                key={grupo.id}
                className={`${styles.grupo} ${alvoCategoria ? styles.grupoAlvo : ''} ${alvoGrupo ? styles.dropAntes : ''} ${recusaArrasto(grupo) ? styles.grupoBloqueado : ''}`}
                title={recusaArrasto(grupo) ? t('plano.tipoIncompativel') : undefined}
                onDragOver={(e) => {
                    if (!podeSoltarCategoria(grupo)) return;
                    e.preventDefault();
                    setAlvoDrop(`grupo:${grupo.id}`);
                }}
                onDragLeave={(e) => {
                    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
                    setAlvoDrop((atual) => (atual === `grupo:${grupo.id}` ? null : atual));
                }}
                onDrop={(e) => {
                    if (!podeSoltarCategoria(grupo) || !arrasto) return;
                    e.preventDefault();
                    moverCategoria(arrasto.id, grupo.id, null);
                    encerrarArrasto();
                }}
            >
                <div
                    className={styles.grupoHeader}
                    draggable={organizando}
                    onClick={() => toggleGrupo(grupo.id)}
                    onDragStart={(e) => {
                        if (!organizando) return;
                        e.stopPropagation();
                        e.dataTransfer.effectAllowed = 'move';
                        e.dataTransfer.setData('text/plain', grupo.id);
                        setArrasto({ kind: 'grupo', id: grupo.id, tipo: grupo.tipo });
                    }}
                    onDragEnd={encerrarArrasto}
                    onDragOver={(e) => {
                        if (!podeSoltarGrupo(grupo)) return;
                        e.preventDefault();
                        e.stopPropagation();
                        setAlvoDrop(`ordem:${grupo.id}`);
                    }}
                    onDrop={(e) => {
                        if (!podeSoltarGrupo(grupo) || !arrasto) return;
                        e.preventDefault();
                        e.stopPropagation();
                        moverGrupo(arrasto.id, grupo.id);
                        encerrarArrasto();
                    }}
                >
                    <div className={styles.grupoTitulo}>
                        {organizando && <FiMove className={styles.handle} size={14} aria-hidden />}
                        {expandido ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />}
                        <h4 title={t(grupo.nome)}>{t(grupo.nome)}</h4>
                    </div>
                    <div className={styles.grupoMeta}>
                        <span>{contarCategorias(grupo.categorias.length)}</span>
                        <div className={styles.actions}>
                            {canEdit && (
                                <button
                                    type="button"
                                    className={`${styles.actionBtn} ${styles.edit}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        openModal('grupo', grupo);
                                    }}
                                    aria-label={t('plano.editarGrupo')}
                                >
                                    <FiEdit2 size={16} />
                                </button>
                            )}
                            {canDelete && (
                                <button
                                    type="button"
                                    className={`${styles.actionBtn} ${styles.delete} ${bloqueado ? styles.disabled : ''}`}
                                    title={bloqueado ? t('plano.exclusaoBloqueada') : undefined}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDelete('grupo', grupo);
                                    }}
                                    aria-label={t('common.excluir')}
                                >
                                    <FiTrash2 size={16} />
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {expandido && (
                    <div className={styles.categoriasList}>
                        {grupo.categorias.length === 0 ? (
                            <p className={styles.grupoVazio}>
                                {podeSoltarCategoria(grupo) ? t('plano.soltarAqui') : t('plano.nenhumaCatNoGrupo')}
                            </p>
                        ) : (
                            grupo.categorias.map((categoria, i) => renderCategoria(grupo, categoria, i))
                        )}
                    </div>
                )}
            </div>
        );
    };

    const renderSecao = (tipo: 'ENTRADA' | 'SAIDA') => {
        const lista = gruposExibidos.filter((g) => (tipo === 'ENTRADA' ? ehReceita(g.tipo) : !ehReceita(g.tipo)));
        const totalCategorias = lista.reduce((acc, g) => acc + g.categorias.length, 0);

        return (
            <div className={styles.section}>
                <div className={styles.secaoHeader}>
                    <h3 className={tipo === 'ENTRADA' ? styles.tituloReceita : styles.tituloDespesa}>
                        {t(tipo === 'ENTRADA' ? 'plano.receitas' : 'plano.despesas')}
                    </h3>
                    <span className={styles.secaoResumo}>
                        {t('plano.resumoSecao', {
                            cat: contarCategorias(totalCategorias),
                            grp: contarGrupos(lista.length),
                        })}
                    </span>
                </div>

                {lista.length === 0 ? (
                    <p className={styles.grupoVazio}>{t('plano.nenhumGrupo')}</p>
                ) : (
                    lista.map(renderGrupo)
                )}
            </div>
        );
    };

    if (loading) {
        return (
            <div className={styles.layout}>
                <Sidebar />
                <main className={styles.main}>
                    <Header title={t('plano.titulo')} subtitle={t('plano.subtitulo')} ocultarSeletorFilial />
                    <div className={styles.content}>
                        <div className={styles.loading}>
                            <FiLoader className="animate-spin" size={32} />
                            <span>{t('common.carregando')}</span>
                        </div>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className={styles.layout}>
            <Sidebar />
            <main className={styles.main}>
                <Header title={t('plano.titulo')} subtitle={t('plano.subtitulo')} ocultarSeletorFilial />
                <div className={styles.content}>
                    {nomeEmpresa && (
                        <div className={styles.faixaEmpresa}>
                            {t('plano.faixaEmpresa', { empresa: nomeEmpresa })}
                        </div>
                    )}

                    {resumo && (
                        <div className={styles.summaryCards}>
                            <div className={styles.summaryCard}>
                                <div className={styles.label}>{t('plano.categorias')}</div>
                                <div className={styles.value}>{resumo.totalCategorias}</div>
                            </div>
                            <div className={styles.summaryCard}>
                                <div className={styles.label}>{t('plano.grupos')}</div>
                                <div className={styles.value}>{resumo.totalGrupos}</div>
                            </div>
                            <div className={styles.summaryCard}>
                                <div className={styles.label}>{t('plano.formasPagamento')}</div>
                                <div className={styles.value}>{resumo.totalFormasPagamento}</div>
                            </div>
                            <div className={styles.summaryCard}>
                                <div className={styles.label}>{t('plano.entradas')}</div>
                                <div className={`${styles.value} ${styles.entrada}`}>{resumo.categoriasEntrada}</div>
                            </div>
                            <div className={styles.summaryCard}>
                                <div className={styles.label}>{t('plano.saidas')}</div>
                                <div className={`${styles.value} ${styles.saida}`}>{resumo.categoriasSaida}</div>
                            </div>
                        </div>
                    )}

                    <div className={styles.tabs}>
                        <button
                            className={`${styles.tab} ${activeTab === 'categorias' ? styles.active : ''}`}
                            onClick={() => trocarAba('categorias')}
                        >
                            {t('plano.categorias')}
                        </button>
                        <button
                            className={`${styles.tab} ${activeTab === 'formasPagamento' ? styles.active : ''}`}
                            onClick={() => trocarAba('formasPagamento')}
                        >
                            {t('plano.formasPagamento')}
                        </button>
                    </div>

                    {activeTab === 'categorias' && (
                        <>
                            <div className={styles.toolbar}>
                                {canEdit && (
                                    <label className={styles.toggle}>
                                        <input
                                            type="checkbox"
                                            checked={organizando}
                                            onChange={alternarOrganizar}
                                        />
                                        <span className={styles.toggleTrack} aria-hidden />
                                        <span className={styles.toggleLabel}>{t('plano.organizarGrupos')}</span>
                                    </label>
                                )}

                                <div className={styles.toolbarActions}>
                                    {canCreate && (
                                        <button
                                            type="button"
                                            className={styles.secondaryBtn}
                                            onClick={() => openModal('grupo')}
                                        >
                                            <FiFolderPlus size={16} />
                                            {t('plano.novoGrupo')}
                                        </button>
                                    )}
                                    {canCreate && (
                                        <button
                                            type="button"
                                            className={styles.addBtn}
                                            onClick={() => openModal('categoria')}
                                            disabled={grupos.length === 0}
                                            title={grupos.length === 0 ? t('plano.nenhumGrupo') : undefined}
                                        >
                                            <FiPlus size={16} />
                                            {t('plano.novaCategoria')}
                                        </button>
                                    )}
                                </div>
                            </div>

                            {organizando && (
                                <div className={styles.organizarBar}>
                                    <span className={styles.organizarAjuda}>{t('plano.organizarAjuda')}</span>
                                    <div className={styles.organizarAcoes}>
                                        {temPendencias && (
                                            <span className={styles.pendentes}>
                                                {t(alteracoes.total === 1 ? 'plano.pendentes' : 'plano.pendentes_plural', {
                                                    n: String(alteracoes.total),
                                                })}
                                            </span>
                                        )}
                                        <button
                                            type="button"
                                            className={styles.cancelBtn}
                                            onClick={descartarOrganizacao}
                                            disabled={salvandoOrganizacao}
                                        >
                                            {t('plano.descartarOrganizacao')}
                                        </button>
                                        <button
                                            type="button"
                                            className={styles.submitBtn}
                                            onClick={() => salvarOrganizacao()}
                                            disabled={!temPendencias || salvandoOrganizacao}
                                        >
                                            {salvandoOrganizacao ? t('common.salvando') : t('plano.salvarOrganizacao')}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {gruposExibidos.length === 0 ? (
                                <div className={styles.section}>
                                    <div className={styles.emptyState}>
                                        <p>{t('plano.nenhumGrupo')}</p>
                                        {canCreate && (
                                            <button type="button" className={styles.addBtn} onClick={() => openModal('grupo')}>
                                                <FiPlus size={16} />
                                                {t('plano.criarPrimeiroGrupo')}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {renderSecao('ENTRADA')}
                                    {renderSecao('SAIDA')}
                                </>
                            )}
                        </>
                    )}

                    {activeTab === 'formasPagamento' && (
                        <div className={styles.section}>
                            <div className={styles.sectionHeader}>
                                <h3>{t('plano.formasPagamento')}</h3>
                                {canCreate && (
                                    <button className={styles.addBtn} onClick={() => openModal('formaPagamento')}>
                                        <FiPlus size={16} />
                                        {t('plano.novaFormaPagamento')}
                                    </button>
                                )}
                            </div>

                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>{t('common.nome')}</th>
                                        <th>{t('common.descricao')}</th>
                                        <th>{t('common.acoes')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {formasPagamento.map((fp) => (
                                        <tr key={fp.id}>
                                            <td className={styles.cellNome}>{t(fp.nome)}</td>
                                            <td className={styles.cellDescricao}>{fp.descricao || '-'}</td>
                                            <td>
                                                <div className={styles.actions}>
                                                    {canEdit && (
                                                        <button
                                                            className={`${styles.actionBtn} ${styles.edit}`}
                                                            onClick={() => openModal('formaPagamento', fp)}
                                                        >
                                                            <FiEdit2 size={16} />
                                                        </button>
                                                    )}
                                                    {canDelete && (
                                                        <button
                                                            className={`${styles.actionBtn} ${styles.delete} ${(fp.lancamentosVinculados ?? 0) > 0 ? styles.disabled : ''}`}
                                                            title={(fp.lancamentosVinculados ?? 0) > 0 ? t('plano.exclusaoBloqueada') : undefined}
                                                            onClick={() => handleDelete('formaPagamento', fp)}
                                                        >
                                                            <FiTrash2 size={16} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {showModal && (
                        <div className={styles.modalOverlay} onClick={closeModal}>
                            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                                <div className={styles.modalHeader}>
                                    <h3>
                                        {modalType === 'grupo'
                                            ? t(editingItem ? 'plano.editarGrupo' : 'plano.novoGrupo')
                                            : modalType === 'categoria'
                                                ? t(editingItem ? 'plano.editarCategoria' : 'plano.novaCategoria')
                                                : t(editingItem ? 'plano.editarFormaPagamento' : 'plano.novaFormaPagamento')}
                                    </h3>
                                    <button className={styles.closeBtn} onClick={closeModal}>
                                        <FiX size={20} />
                                    </button>
                                </div>

                                {editingItem && (editingItem.lancamentosVinculados ?? 0) > 0 && (
                                    <div className={styles.linkedNote}>
                                        {t('plano.vinculadosInfo', { n: String(editingItem.lancamentosVinculados) })}
                                    </div>
                                )}

                                <form onSubmit={handleSubmit}>
                                    {modalType === 'grupo' && (
                                        <div className={styles.formGroup}>
                                            <label>{t('common.tipo')}</label>
                                            <SearchableSelect
                                                value={formData.tipo}
                                                onChange={(v) => setFormData({ ...formData, tipo: v as TipoMovimento })}
                                                required
                                                fullWidth
                                                options={[
                                                    { value: 'ENTRADA', label: t('common.entrada') },
                                                    { value: 'SAIDA', label: t('common.saida') },
                                                ]}
                                            />
                                        </div>
                                    )}

                                    {modalType === 'categoria' && (
                                        <div className={styles.formGroup}>
                                            <label>{t('plano.grupoVinculado')}</label>
                                            <SearchableSelect
                                                options={grupos.map((g) => ({
                                                    value: g.id,
                                                    label: t(g.nome),
                                                    // Mesma tag colorida do filtro de grupo nos relatorios:
                                                    // o tipo se le de relance, sem precisar do sufixo no nome.
                                                    prefix: (
                                                        <span className={`${styles.tipoBadge} ${ehReceita(g.tipo) ? styles.tipoBadgeReceita : styles.tipoBadgeDespesa}`}>
                                                            {ehReceita(g.tipo) ? 'RECEITA' : 'DESPESA'}
                                                        </span>
                                                    ),
                                                }))}
                                                value={formData.grupoId}
                                                onChange={(v) => setFormData({ ...formData, grupoId: v })}
                                                placeholder={t('plano.pesquisarGrupo')}
                                                required
                                                fullWidth
                                            />
                                        </div>
                                    )}

                                    <div className={styles.formGroup}>
                                        <label>{t('common.nome')}</label>
                                        <input
                                            type="text"
                                            value={formData.nome}
                                            onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                                            placeholder={t('common.nome')}
                                            required
                                            maxLength={50}
                                        />
                                    </div>

                                    <div className={styles.formGroup}>
                                        <label>{t('common.descricao')}</label>
                                        <textarea
                                            value={formData.descricao}
                                            onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                                            placeholder={t('plano.descricaoOpcional')}
                                            maxLength={100}
                                        />
                                    </div>

                                    <div className={styles.formActions}>
                                        <button type="button" className={styles.cancelBtn} onClick={closeModal}>
                                            {t('common.cancelar')}
                                        </button>
                                        <button type="submit" className={styles.submitBtn} disabled={saving}>
                                            {saving ? t('common.salvando') : editingItem ? t('common.salvarAlteracoes') : t('common.adicionar')}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};
